import { num } from '../i18n';
import type { ResearchDelivery } from './delivery';
import type { ReplayAdapter } from './replay-adapter';

export interface ReplayControlCopy {
  title: string;
  explanation: string;
  retention: string;
  retentionOne: string;
  agree: string;
  start: string;
  stop: string;
  stopAndClear: string;
  reset: string;
  starting: string;
  stopping: string;
  resetting: string;
  unavailable: string;
  inactive: string;
  active: string;
  stopped: string;
  cleared: string;
  researchRequired: string;
  notActivated: string;
}

/** Separate, visible-scope consent and stop/reset controls for visual replay. */
export function mountReplayControls(parent: HTMLElement, delivery: ResearchDelivery, replay: ReplayAdapter, copy: ReplayControlCopy): () => void {
  const section = document.createElement('section');
  section.className = 'replay-controls';
  section.setAttribute('aria-labelledby', 'replay-controls-title');
  section.hidden = true;
  const title = document.createElement('h3');
  title.id = 'replay-controls-title';
  title.textContent = copy.title;
  const explanation = document.createElement('p');
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const consentLabel = document.createElement('label');
  const consent = document.createElement('input');
  consent.type = 'checkbox';
  consentLabel.append(consent, document.createTextNode(copy.agree));
  const start = document.createElement('button');
  const stop = document.createElement('button');
  const stopAndClear = document.createElement('button');
  const reset = document.createElement('button');
  let actionStatus = '';
  /** The action currently awaiting its server round-trip, if any; render() re-enables on completion. */
  let inFlight: 'start' | 'stop' | 'clear' | 'reset' | null = null;
  for (const button of [start, stop, stopAndClear, reset]) button.type = 'button';

  const render = () => {
    const state = replay.status;
    const research = delivery.status;
    section.hidden = !state.eligible;
    // The learner copy states only the retention window on this same server;
    // the frame route and the expiry sweep are deployment detail.
    const days = typeof state.retentionDays === 'number' ? num(state.retentionDays) : num(0);
    const kept = `: ${state.retentionDays === 1 ? copy.retentionOne : copy.retention.replace('{days}', days)}`;
    explanation.textContent = state.enabled ? `${copy.explanation}${kept}` : `${copy.explanation} ${copy.notActivated}`;
    const canRecord = state.eligible && state.enabled && research.active;
    const busy = inFlight !== null;
    inFlight = null;
    consentLabel.hidden = !canRecord || state.active;
    start.hidden = !canRecord || state.active;
    start.disabled = busy || !consent.checked;
    start.textContent = copy.start;
    stop.hidden = !state.active;
    stop.disabled = busy;
    stop.textContent = copy.stop;
    stopAndClear.hidden = !state.active;
    stopAndClear.disabled = busy;
    stopAndClear.textContent = copy.stopAndClear;
    reset.hidden = !state.active;
    reset.disabled = busy;
    reset.textContent = copy.reset;
    if (actionStatus) status.textContent = actionStatus;
    else if (!state.enabled) status.textContent = copy.notActivated;
    else if (!research.active) status.textContent = copy.researchRequired;
    else status.textContent = state.active ? copy.active : copy.inactive;
  };
  const onStart = async () => {
    inFlight = 'start';
    start.disabled = true;
    start.textContent = copy.starting;
    const ok = await replay.optIn();
    actionStatus = ok ? copy.active : copy.unavailable;
    consent.checked = false;
    render();
  };
  const onStop = async () => {
    inFlight = 'stop';
    stop.disabled = true;
    stop.textContent = copy.stopping;
    const ok = await replay.stop();
    actionStatus = ok ? copy.stopped : copy.unavailable;
    render();
  };
  const onStopAndClear = async () => {
    inFlight = 'clear';
    stopAndClear.disabled = true;
    stopAndClear.textContent = copy.stopping;
    const ok = await replay.optOut();
    actionStatus = ok ? copy.cleared : copy.unavailable;
    render();
  };
  const onReset = async () => {
    inFlight = 'reset';
    reset.disabled = true;
    reset.textContent = copy.resetting;
    const ok = await replay.reset();
    actionStatus = ok ? copy.active : copy.unavailable;
    render();
  };
  const onReplayState = () => { actionStatus = ''; render(); };
  const onResearch = () => { actionStatus = ''; void replay.start().then(render); };
  consent.addEventListener('change', render);
  start.addEventListener('click', onStart);
  stop.addEventListener('click', onStop);
  stopAndClear.addEventListener('click', onStopAndClear);
  reset.addEventListener('click', onReset);
  window.addEventListener('li-replay-state', onReplayState);
  window.addEventListener('li-research-state', onResearch);
  section.append(title, explanation, status, consentLabel, start, stop, reset, stopAndClear);
  parent.append(section);
  void replay.start().then(render);
  render();
  return () => {
    consent.removeEventListener('change', render);
    start.removeEventListener('click', onStart);
    stop.removeEventListener('click', onStop);
    stopAndClear.removeEventListener('click', onStopAndClear);
    reset.removeEventListener('click', onReset);
    window.removeEventListener('li-replay-state', onReplayState);
    window.removeEventListener('li-research-state', onResearch);
    section.remove();
  };
}
